/**
 * The merchant plan ladder, mirrored from the server's PLAN_KEYS.
 *
 * Labels and ordering only — prices, case allowances and seat counts come from
 * the API (`merchantPlanPrices` in SystemConfig), never from here.
 */
export const PLAN_OPTIONS = [
  { value: 'pilot', label: 'Pilot' },
  { value: 'merchant', label: 'Merchant' }
];

export const PLAN_KEYS = PLAN_OPTIONS.map((p) => p.value);

/**
 * Plans a merchant can buy without talking to sales. Mirrors SELF_SERVE_PLANS
 * on the server: Pilot is provisioned by the team, not bought.
 */
export const SELF_SERVE_PLAN_KEYS = ['merchant'];

export const planLabel = (value) =>
  PLAN_OPTIONS.find((p) => p.value === value)?.label || value || '—';

/**
 * £ amount for display, without dropping cents on a non-round price. A plain
 * `.toFixed(0)` silently rounds 49.99 up to "£50" — this shows two decimals
 * only when the amount actually carries a fraction (£50 stays "£50", £49.99
 * stays "£49.99").
 */
export const formatPrice = (value) => {
  const n = Number(value) || 0;
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
};

export const isSelfServePlan = (value) => SELF_SERVE_PLAN_KEYS.includes(value);

export const planTone = (value) => {
  if (value === 'pilot') return 'info';
  return 'success';
};

/**
 * Whether `merchant.plan === 'pilot'` reflects a pilot that was actually
 * provisioned (trial.startAt set), rather than just the schema default every
 * Merchant document is born with. A record can exist — and sit on 'pilot' —
 * before anyone has chosen anything at all: a customer's first case against
 * a store ParcelBuddy hasn't seen yet auto-creates one (merchantResolutionService),
 * with no plan decision behind it. Reading `plan` alone there reports a pilot
 * that was never started.
 */
const hasProvisionedPilot = (merchant) => Boolean(merchant?.trial?.startAt);

/**
 * The plan/billing badge for a merchant, keyed off billingModel rather than
 * plan alone. Pay-per-case merchants have no tier — moving off a pilot into
 * pay-per-case never changes `plan` (it stays a historical record of which
 * pilot led here), so reading `plan` directly would keep showing "Pilot"
 * forever. Subscription already keeps `plan` in sync when a merchant
 * activates, so it's read straight through.
 */
export const billingLabel = (merchant) => {
  if (!merchant) return '—';
  if (merchant.billingModel === 'pay_per_case') return 'Pay per case';
  if (merchant.plan === 'pilot' && !hasProvisionedPilot(merchant)) return 'No plan chosen';
  return planLabel(merchant.plan);
};

export const billingTone = (merchant) => {
  if (!merchant) return 'neutral';
  if (merchant.billingModel === 'pay_per_case') return 'success';
  if (merchant.plan === 'pilot' && !hasProvisionedPilot(merchant)) return 'neutral';
  return planTone(merchant.plan);
};

/**
 * Whether this merchant ran a pilot at some point but has since moved onto
 * paid billing — the pilot's trial/case fields are kept as history rather
 * than cleared, so this is the one place that distinguishes "still on the
 * pilot" from "used to be, now paying".
 */
export const pilotPreviouslyUsed = (merchant) =>
  Boolean(merchant?.trial?.startAt) && merchant?.billingModel !== 'free';

/**
 * Visual accent per plan tier, shared by every surface that shows the plan
 * ladder — the public pricing page, the activation modal, and the portal
 * upgrade grid — so a plan reads the same wherever a merchant sees it.
 *
 * Only brand, gold and ink are used to tell tiers apart: success/warning/
 * danger/info are reserved for state elsewhere in the app (an active
 * subscription, a pending invite, an error), so reusing them here to mean
 * "this is the Business tier" would collide with that meaning. info is the
 * one deliberate exception — Pilot reuses it because "Trial" is already an
 * info-toned state on the admin pricing screen, so it stays a state color,
 * not a new category one.
 */
const PLAN_ACCENTS = {
  pilot: {
    badgeBg: 'bg-info-50',
    badgeText: 'text-info-700',
    ring: 'ring-info-100',
    border: 'border-info-200',
    borderSoft: 'border-info-100',
    wash: 'from-info-50 to-white'
  },
  merchant: {
    badgeBg: 'bg-gold-100',
    badgeText: 'text-gold-800',
    ring: 'ring-gold-200',
    border: 'border-gold-300',
    borderSoft: 'border-gold-200',
    wash: 'from-gold-50 to-white'
  }
};

export const planAccent = (key) => PLAN_ACCENTS[key] || PLAN_ACCENTS.merchant;

/**
 * Pay-per-case is no longer sold. Nothing on the merchant side offers it, but
 * the handling is kept rather than deleted: a merchant could still be on it
 * from before, and admin reporting reads the same fields. With this off, such
 * a merchant is shown the plan grid instead of "continue paying per case" —
 * which is the direction they should be moving anyway. Flip to bring it back.
 */
export const SHOW_PAY_PER_CASE = false;

export const merchantRoleLabel = (value) => (value === 'admin' ? 'Admin' : 'Agent');

export const userStatusTone = (status) => {
  if (status === 'active') return 'success';
  if (status === 'invited') return 'warning';
  return 'neutral';
};
